import { useCallback, useEffect, useState } from 'react';
import {
  detectLikelyIncognito,
  detectPwaPlatform,
  isStandaloneDisplay,
  PWA_ORIGIN,
  readInstallDismissed,
  writeInstallDismissed,
  type PwaPlatform,
} from '@/lib/pwaInstall';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Props = {
  /** `banner` = barra compacta en la app; `card` = bloque en login. */
  variant?: 'banner' | 'card';
};

function platformLabel(platform: PwaPlatform): string {
  if (platform === 'ios') return 'iPhone o iPad';
  if (platform === 'android') return 'Android';
  return 'este dispositivo';
}

function ManualSteps({ platform }: { platform: PwaPlatform }) {
  if (platform === 'ios') {
    return (
      <ol className="pwa-install-prompt__steps">
        <li>
          Tocá el botón <strong>Compartir</strong> (□ con flecha ↑) en Safari o Chrome.
        </li>
        <li>
          Elegí <strong>Agregar a pantalla de inicio</strong> (o <strong>Añadir a inicio</strong>).
        </li>
        <li>
          Confirmá con <strong>Agregar</strong>. Abrí la app desde el ícono en la pantalla de inicio.
        </li>
      </ol>
    );
  }
  if (platform === 'android') {
    return (
      <ol className="pwa-install-prompt__steps">
        <li>
          Abrí el menú <strong>⋮</strong> de Chrome (arriba a la derecha).
        </li>
        <li>
          Elegí <strong>Instalar aplicación</strong>, <strong>Instalar app</strong> o{' '}
          <strong>Añadir a la pantalla de inicio</strong>.
        </li>
        <li>Si no aparece, interactuá con la página (p. ej. entrá con tu token) y volvé al menú.</li>
      </ol>
    );
  }
  return (
    <ol className="pwa-install-prompt__steps">
      <li>
        En Chrome o Edge, buscá el ícono <strong>Instalar</strong> (⊕) en la barra de direcciones.
      </li>
      <li>
        Si no está, abrí el menú <strong>⋮</strong> → <strong>Instalar Interactive Talker…</strong>
      </li>
      <li>
        En móvil, usá <strong>{PWA_ORIGIN}</strong> en Safari (iOS) o Chrome (Android).
      </li>
    </ol>
  );
}

export function InstallAppPrompt({ variant = 'card' }: Props) {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandaloneDisplay);
  const [dismissed, setDismissed] = useState(readInstallDismissed);
  const [installing, setInstalling] = useState(false);
  const [platform] = useState(detectPwaPlatform);
  const [incognito, setIncognito] = useState<boolean | null>(null);
  const [expanded, setExpanded] = useState(variant === 'card');

  useEffect(() => {
    const onInstallable = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
      setExpanded(true);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallEvent(null);
    };
    const onDisplayMode = () => setInstalled(isStandaloneDisplay());
    window.addEventListener('beforeinstallprompt', onInstallable);
    window.addEventListener('appinstalled', onInstalled);
    window.matchMedia('(display-mode: standalone)').addEventListener('change', onDisplayMode);
    return () => {
      window.removeEventListener('beforeinstallprompt', onInstallable);
      window.removeEventListener('appinstalled', onInstalled);
      window.matchMedia('(display-mode: standalone)').removeEventListener('change', onDisplayMode);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const likely = await detectLikelyIncognito();
      if (!cancelled) setIncognito(likely);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const runInstall = useCallback(async () => {
    if (!installEvent) {
      setExpanded(true);
      return;
    }
    setInstalling(true);
    try {
      await installEvent.prompt();
      const { outcome } = await installEvent.userChoice;
      if (outcome === 'accepted') setInstalled(true);
      setInstallEvent(null);
    } finally {
      setInstalling(false);
    }
  }, [installEvent]);

  const dismiss = useCallback(() => {
    writeInstallDismissed();
    setDismissed(true);
  }, []);

  if (installed || dismissed) return null;

  const rootClass = [
    'pwa-install-prompt',
    variant === 'banner' ? 'pwa-install-prompt--banner' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (variant === 'banner' && !expanded) {
    return (
      <div className={rootClass} role="region" aria-label="Instalar aplicación">
        <div className="pwa-install-prompt__banner-row">
          <p className="pwa-install-prompt__banner-text">
            Instalá la app en {platformLabel(platform)} para usarla a pantalla completa y recibir notificaciones.
          </p>
          <div className="pwa-install-prompt__banner-actions">
            <button
              type="button"
              className="pwa-btn pwa-btn--primary pwa-btn--sm"
              disabled={installing}
              onClick={() => (installEvent && !incognito ? void runInstall() : setExpanded(true))}
            >
              {installing ? '…' : installEvent && !incognito ? 'Instalar' : 'Cómo instalar'}
            </button>
            <button type="button" className="pwa-install-prompt__banner-close" aria-label="Ocultar por ahora" onClick={dismiss}>
              ×
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass} role="region" aria-label="Instalar aplicación">
      <p className="pwa-install-prompt__text">
        <strong>Instalá Interactive Talker</strong> en {platformLabel(platform)} para abrirla como app nativa y
        recibir notificaciones push.
      </p>

      {incognito ?
        <p className="pwa-install-prompt__warn" role="status">
          Las ventanas de incógnito <strong>no permiten instalar</strong> aplicaciones. Abrí{' '}
          <a href={PWA_ORIGIN} className="pwa-install-prompt__link">
            {PWA_ORIGIN}
          </a>{' '}
          en una ventana normal del navegador.
        </p>
      : null}

      {installEvent && !incognito ?
        <div className="pwa-install-prompt__actions">
          <button
            type="button"
            className="pwa-btn pwa-btn--primary"
            disabled={installing}
            onClick={() => void runInstall()}
          >
            {installing ? 'Abriendo…' : 'Instalar app'}
          </button>
        </div>
      : null}

      {!incognito ?
        <>
          <p className="pwa-install-prompt__hint">
            {installEvent ?
              'También podés usar las instrucciones del sistema:'
            : platform === 'ios' ?
              'En iPhone/iPad la instalación se hace desde Compartir → Agregar a pantalla de inicio:'
            : platform === 'android' ?
              'Si no aparece el botón de arriba, seguí estos pasos en Chrome:'
            : 'Seguí estos pasos en tu navegador:'}
          </p>
          <ManualSteps platform={platform} />
        </>
      : null}

      <div className="pwa-install-prompt__actions pwa-install-prompt__actions--footer">
        <button type="button" className="pwa-btn pwa-btn--secondary" onClick={dismiss}>
          Ahora no
        </button>
      </div>
    </div>
  );
}
